import * as R from 'ramda';
import React, { useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
// components
import { EditReport } from '../components/edit-report';
import { openModal } from '../components/modal/actions';
// helpers/constants
import * as G from '../helpers';
import * as GC from '../constants';
// report-common
import { getReportSelectors } from '../report-common';
//////////////////////////////////////////////////

const useReport = ({
  A,
  type,
  selectors,
  reportModalMaxWidth,
  additionalReportProps,
  useDefaultReportModal,
}) => {
  const dispatch = useDispatch();

  const {
    makeSelectUsedReport,
    makeSelectPagination,
    makeSelectTotalCount,
    makeSelectListLoading,
    makeSelectPageVisited,
    makeSelectReportStatus,
    makeSelectFilterParams,
    makeSelectTitleSortValues,
    makeSelectAvailableReports,
    makeSelectTableTitleFilters,
  } = G.isNotNil(selectors) ? selectors : getReportSelectors(type);

  const [originalReport, setOriginalReport] = useState(null);
  const [reportFromPrompt, setReportFromPrompt] = useState(null);

  const loading = useSelector(makeSelectListLoading());
  const totalCount = useSelector(makeSelectTotalCount());
  const pagination = useSelector(makeSelectPagination());
  const pageVisited = useSelector(makeSelectPageVisited());
  const selectedReport = useSelector(makeSelectUsedReport());
  const filterParams = useSelector(makeSelectFilterParams());
  const requestPending = useSelector(makeSelectReportStatus());
  const reportList = useSelector(makeSelectAvailableReports());
  const titleSortValues = useSelector(makeSelectTitleSortValues());
  const tableTitleFilters = useSelector(makeSelectTableTitleFilters());

  const setReports = useCallback((props) => dispatch(A.setReports(props)), []);
  const setUsedReport = useCallback((props) => dispatch(A.setUsedReport(props)), []);
  const cleanQuickFilter = useCallback((props) => dispatch(A.cleanQuickFilter(props)), []);
  const getItemListRequest = useCallback((props) => dispatch(A.getItemListRequest(props)), []);
  const createReportRequest = useCallback((props) => dispatch(A.createReportRequest(props)), []);
  const updateReportRequest = useCallback((props) => dispatch(A.updateReportRequest(props)), []);
  const setQuickFilterParams = useCallback((props) => dispatch(A.setQuickFilterParams(props)), []);
  const resetListAndPagination = useCallback((props) => dispatch(A.resetListAndPagination(props)), []);
  const changeDefaultReportRequest = useCallback((props) => dispatch(A.changeDefaultReportRequest(props)), []);

  const selectItem = useCallback((guid) => (
    A.selectItem ? dispatch(A.selectItem(guid)) : null
  ), []);

  const handleSelectReport = useCallback((reportGuid) => {
    const selectedReport = R.find(R.propEq(reportGuid, GC.FIELD_GUID), reportList);

    setUsedReport(selectedReport);
    getItemListRequest();
  }, [reportList, setUsedReport, getItemListRequest]);

  const handleEditReport = useCallback((fields) => {
    const additionalReportPropsToUse = G.isFunction(additionalReportProps)
      ? additionalReportProps({ getItemListRequest })
      : R.or(additionalReportProps, {});

    const modalContent = (
      <EditReport
        fields={fields}
        setReport={setUsedReport}
        usedReport={selectedReport}
        requestPending={requestPending}
        onReportSet={getItemListRequest}
        createReportRequest={createReportRequest}
        updateReportRequest={updateReportRequest}
        {...additionalReportPropsToUse}
      />
    );

    const modal = useDefaultReportModal
      ? G.getDefaultReportModal(modalContent, reportModalMaxWidth)
      : {
        options: {
          height: 'auto',
          maxWidth: '98vw',
          width: 'fit-content',
        },
        component: modalContent,
      };

    dispatch(openModal(modal));
  }, [
    dispatch,
    selectedReport,
    requestPending,
    setUsedReport,
    getItemListRequest,
    createReportRequest,
    updateReportRequest,
    reportModalMaxWidth,
    additionalReportProps,
    useDefaultReportModal,
  ]);

  const handleListRequest = useCallback(
    (isInitial = false) => getItemListRequest(isInitial),
    [getItemListRequest],
  );

  const getQuickFilteredListRequest = useCallback(() => {
    resetListAndPagination();
    getItemListRequest(true);
  }, [getItemListRequest, resetListAndPagination]);

  const handleChangeReportParams = useCallback((data) => {
    setReportFromPrompt(data);
  }, []);

  const handleSetUsedReport = useCallback((setPromptStatus) => {
    if (R.and(
      G.isNotNilAndNotEmpty(reportFromPrompt),
      G.notEquals(selectedReport, reportFromPrompt),
    )) {
      setOriginalReport(selectedReport);
      setUsedReport(reportFromPrompt);
      getItemListRequest(true);
    }

    if (G.isFunction(setPromptStatus)) setPromptStatus(false);
  }, [getItemListRequest, reportFromPrompt, selectedReport, setUsedReport]);

  const handleCleanFilter = useCallback(() => {
    cleanQuickFilter();
    resetListAndPagination();
    getItemListRequest(true);
  }, [cleanQuickFilter, getItemListRequest, resetListAndPagination]);

  const handleTableTitleFilter = useCallback((data) => {
    G.handleTableTitleFilter2(data, A, dispatch);

    getItemListRequest();
  }, []);

  return {
    // data
    loading,
    totalCount,
    pagination,
    reportList,
    // actions
    setReports,
    selectItem,
    pageVisited,
    filterParams,
    setUsedReport,
    selectedReport,
    originalReport,
    requestPending,
    titleSortValues,
    reportFromPrompt,
    cleanQuickFilter,
    handleEditReport,
    tableTitleFilters,
    handleCleanFilter,
    handleListRequest,
    handleSelectReport,
    getItemListRequest,
    createReportRequest,
    updateReportRequest,
    handleSetUsedReport,
    setQuickFilterParams,
    handleTableTitleFilter,
    resetListAndPagination,
    handleChangeReportParams,
    changeDefaultReportRequest,
    getQuickFilteredListRequest,
  };
};

export default useReport;
